import { supabase as _supabase } from "@/integrations/supabase/client";

// calendar_events / meetings tables not yet in generated types — cast to any
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const supabase = _supabase as any;

export interface EnsureMeetingResult {
  meeting_id: string;
  room_url: string;
  room_name: string;
  consultant_token: string;
  client_join_url: string;
  reused: boolean;
}

interface EnsureMeetingInput {
  calendar_event_id: string;
  daily_room_url?: string | null;
  daily_room_name?: string | null;
  contact_id?: string | null;
  deal_id?: string | null;
  end_at?: string | null;
  attendee_ids?: string[] | null;
}

/**
 * Single source of truth for getting (or creating) a Daily meeting
 * tied to a calendar_event. Guarantees both "Copy Link" and "Join"
 * point to the SAME Daily room — preventing duplicate-room bugs
 * where consultant and client end up in different rooms.
 */
export async function ensureMeetingForEvent(
  input: EnsureMeetingInput
): Promise<EnsureMeetingResult> {
  // 1) If event already has a Daily room, look up the meeting record + host token
  if (input.daily_room_url || input.daily_room_name) {
    let meeting: { id: string; daily_room_url: string; daily_room_name: string } | null = null;

    if (input.daily_room_name) {
      const { data } = await supabase
        .from("meetings")
        .select("id, daily_room_url, daily_room_name")
        .eq("daily_room_name", input.daily_room_name)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      meeting = data;
    }
    if (!meeting && input.daily_room_url) {
      const { data } = await supabase
        .from("meetings")
        .select("id, daily_room_url, daily_room_name")
        .eq("daily_room_url", input.daily_room_url)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      meeting = data;
    }

    if (meeting) {
      const { data: participant } = await supabase
        .from("meeting_participants")
        .select("daily_token")
        .eq("meeting_id", meeting.id)
        .in("role", ["host", "co-host"])
        .not("daily_token", "is", null)
        .limit(1)
        .maybeSingle();

      const publicSiteUrl = window.location.origin;
      return {
        meeting_id: meeting.id,
        room_url: meeting.daily_room_url,
        room_name: meeting.daily_room_name,
        consultant_token: participant?.daily_token ?? "",
        client_join_url: `${publicSiteUrl}/meet/${meeting.id}`,
        reused: true,
      };
    }
  }

  // 2) No room yet (or stale reference) — create idempotently via edge function
  const { data, error } = await supabase.functions.invoke("create-daily-room", {
    body: {
      calendar_event_id: input.calendar_event_id,
      contact_id: input.contact_id || undefined,
      deal_id: input.deal_id || undefined,
      end_at: input.end_at || undefined,
      attendee_ids: input.attendee_ids || [],
      type: "scheduled",
    },
  });

  if (error) throw new Error(error.message || "Erro ao criar sala");
  if (data?.error) throw new Error(data.error);

  // Edge function already persists daily_room_url/name to calendar_events,
  // but we double-write here for resilience in case of races.
  await supabase
    .from("calendar_events")
    .update({
      daily_room_url: data.room_url,
      daily_room_name: data.room_name ?? null,
    })
    .eq("id", input.calendar_event_id);

  return {
    meeting_id: data.meeting_id,
    room_url: data.room_url,
    room_name: data.room_name,
    consultant_token: data.consultant_token,
    client_join_url: data.client_join_url,
    reused: false,
  };
}
